/** La coda offline dei voti.
 *
 *  Vive in IndexedDB ma è di proprietà dell'app, non del service worker: il
 *  replay in background si farebbe con la Background Sync API, che Safari
 *  non implementa, e metà del gruppo vota da iPhone. Una coda che riparte
 *  solo su Android sarebbe peggio di una coda dichiaratamente in primo piano.
 *
 *  Il replay è sicuro perché il backend è idempotente — chiavato su
 *  (edizione, giurato, artista, serata, categoria) — e scarta i voti con un
 *  `client_ts` più vecchio di quello già salvato.
 */

import { type DBSchema, type IDBPDatabase, openDB } from "idb"
import { api } from "../api/client"
import type { VotoInviato } from "../api/tipi"

const DB = "giuria-trash"
const NEGOZIO = "voti-in-coda"

/** Il batch accetta al massimo 200 voti per chiamata (`VoteBatchIn`). */
const MAX_PER_BATCH = 200

interface Coda extends DBSchema {
  [NEGOZIO]: {
    key: string
    /** `proprietario` manca solo nelle voci accodate prima degli account. */
    value: { chiave: string; voto: VotoInviato; accodatoIl: number; proprietario?: string }
  }
}

let connessione: Promise<IDBPDatabase<Coda>> | null = null

function apri(): Promise<IDBPDatabase<Coda>> {
  connessione ??= openDB<Coda>(DB, 1, {
    upgrade(db) {
      db.createObjectStore(NEGOZIO, { keyPath: "chiave" })
    },
  })
  return connessione
}

/** La stessa tupla dell'indice unique sul server, preceduta da chi ha votato:
 *  rivotare la stessa cella mentre si è offline sostituisce l'elemento in
 *  coda invece di impilarne un altro, e due persone che usano lo stesso
 *  telefono non si pestano i voti. */
function chiaveDi(voto: VotoInviato, proprietario: string): string {
  return `${proprietario}|${voto.artist_id}|${voto.serata_id ?? ""}|${voto.category}`
}

/** Le voci di questa persona. Quelle senza proprietario vengono da prima
 *  degli account: si comportano come allora e partono con chi c'è. */
async function vociDi(db: IDBPDatabase<Coda>, proprietario: string) {
  return (await db.getAll(NEGOZIO)).filter((v) => (v.proprietario ?? proprietario) === proprietario)
}

export async function accoda(voto: VotoInviato, proprietario: string): Promise<void> {
  const db = await apri()
  await db.put(NEGOZIO, {
    chiave: chiaveDi(voto, proprietario),
    voto,
    accodatoIl: Date.now(),
    proprietario,
  })
}

export async function quantiInCoda(proprietario: string): Promise<number> {
  return (await vociDi(await apri(), proprietario)).length
}

export interface EsitoSvuotamento {
  inviati: number
  /** Voti che il server ha rifiutato per sempre: non si ritentano. */
  rifiutati: { categoria: string; motivo: string }[]
}

/** Rigioca le voci di questa persona. Se la rete manca di nuovo solleva, e la
 *  coda resta intatta. Le voci di altri restano in attesa del loro
 *  proprietario. */
export async function svuota(token: string, proprietario: string): Promise<EsitoSvuotamento> {
  const db = await apri()
  const voci = await vociDi(db, proprietario)
  if (voci.length === 0) return { inviati: 0, rifiutati: [] }

  const esito: EsitoSvuotamento = { inviati: 0, rifiutati: [] }

  for (let i = 0; i < voci.length; i += MAX_PER_BATCH) {
    const lotto = voci.slice(i, i + MAX_PER_BATCH)
    const risposta = await api.votaBatch(
      lotto.map((v) => v.voto),
      token,
    )

    // I risultati tornano nello stesso ordine dei voti inviati: è l'indice
    // a legarli. Fidarsi di (artist_id, category) sarebbe ambiguo, perché
    // lo stesso artista può avere voti in coda per due serate diverse.
    await Promise.all(
      risposta.results.map((r, k) => {
        const voce = lotto[k]
        if (!voce) return
        if (r.ok) esito.inviati += 1
        else {
          // Un rifiuto del batch è definitivo: 409 "troppo tardi", 400
          // categoria o serata sbagliata, 404 non in scaletta. Nessuno di
          // questi migliora ritentando.
          esito.rifiutati.push({ categoria: r.category, motivo: r.error ?? "rifiutato" })
        }
        return db.delete(NEGOZIO, voce.chiave)
      }),
    )
  }

  return esito
}

/** Solo per i test e per il "esci": la coda è legata a un giurato. */
export async function scarta(): Promise<void> {
  await (await apri()).clear(NEGOZIO)
}
